import { Component } from "react";
import { AlertTriangle, RotateCw, Home as HomeIcon } from "lucide-react";

/**
 * Global Error Boundary — prevents the dreaded "white screen of death"
 * when a runtime exception bubbles up to the React root.
 *
 * Usage: wrap <App/> in <ErrorBoundary>.
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Log to console — surfaces in Sentry/any monitoring if connected
        console.error("[ErrorBoundary]", error, info?.componentStack);
    }

    reset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        const msg = String(this.state.error?.message || this.state.error || "Erro desconhecido");
        return (
            <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F8F8F5]" data-testid="error-boundary">
                <div className="max-w-lg w-full bg-white rounded-3xl border border-black/5 shadow-xl p-8 md:p-10 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 border border-amber-100 mb-5">
                        <AlertTriangle className="w-7 h-7 text-amber-500" />
                    </div>
                    <h1 className="font-display text-2xl text-[#1A1A1A]">Ops, algo inesperado aconteceu</h1>
                    <p className="font-body text-sm text-[#5C5C5C] mt-3">
                        Não conseguimos renderizar esta tela. Já registramos o erro. Você pode tentar recarregar ou voltar para o início.
                    </p>
                    <details className="mt-5 text-left">
                        <summary className="cursor-pointer text-xs font-display text-[#5C5C5C]">Detalhes técnicos</summary>
                        <pre className="mt-2 p-3 rounded-lg bg-[#FAFAF7] text-[11px] text-[#5C5C5C] overflow-auto max-h-40 whitespace-pre-wrap break-words">
                            {msg}
                        </pre>
                    </details>
                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                        <button
                            onClick={() => { this.reset(); window.location.reload(); }}
                            data-testid="error-reload"
                            className="btn-primary"
                        >
                            <RotateCw className="w-4 h-4" /> Recarregar página
                        </button>
                        <a href="/home" data-testid="error-home" className="btn-ghost">
                            <HomeIcon className="w-4 h-4" /> Voltar ao início
                        </a>
                    </div>
                </div>
            </div>
        );
    }
}
